import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import toast from 'react-hot-toast';
import {
  User,
  Mail,
  MapPin,
  Calendar,
  AlertTriangle,
  Save,
  X,
  ChevronDown,
  ChevronUp,
  Clock,
  Keyboard,
  Search,
  Check,
} from 'lucide-react';
import { fetchCountries, getCountriesSync, fetchDiplomes, fetchGrades, fetchDepartements, type NomenclatureCountry, type NomenclatureDiplome, type NomenclatureGrade, type NomenclatureDepartement } from '@/services/nomenclatureService';
import { fetchCampuses, type Campus } from '@/services/etablissementService';
import {
  type CommonProfileFields,
  handicapOptions,
  calculateAge,
} from '@/types/profile.types';
import { toDisplayDate, toIsoDate } from '@/utils/dateString';
import { toCountryCode } from '@/shared/constants/countryCode';
import { emergencyRelationSelectOptions } from '@/shared/constants/emergencyRelation';
import { Briefcase, CreditCard, GraduationCap, DollarSign, Building2, Award, BookOpen } from 'lucide-react';

// Staff roles that need the professional information section
// These must match the FRONTEND UserRole enum values
const STAFF_ROLES = [
  'SUPER_ADMIN',
  'DIRECTOR_GENERAL',
  'BRANCH_MANAGER',
  'PEDAGOGICAL_DIRECTOR',
  'ACADEMIC_COORDINATOR',
  'FINANCIAL_MANAGER',
  'MARKETING_MANAGER',
  'COMMERCIAL_MANAGER',
  'PROGRAM_HEAD',
  'TEACHER',
];

// Staff roles that CAN ALSO be professors (excludes STUDENT, PARENT, and SUPER_ADMIN)
// These roles will see the "Êtes-vous aussi professeur?" question
const STAFF_ROLES_WHO_CAN_TEACH = [
  'DIRECTOR_GENERAL',
  'BRANCH_MANAGER',
  'PEDAGOGICAL_DIRECTOR',
  'ACADEMIC_COORDINATOR',
  'FINANCIAL_MANAGER',
  'MARKETING_MANAGER',
  'COMMERCIAL_MANAGER',
  'PROGRAM_HEAD',
];

// RIB validation: Morocco RIB is 24 digits
const validateRIB = (rib: string): boolean => {
  if (!rib) return true; // Optional field
  const cleanRib = rib.replace(/\s/g, '');
  return /^\d{24}$/.test(cleanRib);
};

// Format RIB with spaces for readability (XXXX XXXX XXXX XXXX XXXX XXXX)
const formatRIB = (value: string): string => {
  const cleanValue = value.replace(/\D/g, '').slice(0, 24);
  const groups = cleanValue.match(/.{1,4}/g);
  return groups ? groups.join(' ') : cleanValue;
};

// Format salary with MAD currency
const formatSalaryMAD = (value: string): string => {
  const numericValue = value.replace(/\D/g, '');
  if (!numericValue) return '';
  const number = parseInt(numericValue, 10);
  return number.toLocaleString('fr-MA');
};

// Arabic keyboard layout for virtual keyboard
const ARABIC_KEYBOARD_LAYOUT = [
  ['ض', 'ص', 'ث', 'ق', 'ف', 'غ', 'ع', 'ه', 'خ', 'ح', 'ج'],
  ['ش', 'س', 'ي', 'ب', 'ل', 'ا', 'ت', 'ن', 'م', 'ك', 'ط'],
  ['ئ', 'ء', 'ؤ', 'ر', 'لا', 'ى', 'ة', 'و', 'ز', 'ظ', 'د'],
];

// Country phone codes with flags
const COUNTRY_PHONE_CODES = [
  { code: 'MA', name: 'Maroc', dialCode: '+212', flag: '🇲🇦' },
  { code: 'DZ', name: 'Algérie', dialCode: '+213', flag: '🇩🇿' },
  { code: 'TN', name: 'Tunisie', dialCode: '+216', flag: '🇹🇳' },
  { code: 'FR', name: 'France', dialCode: '+33', flag: '🇫🇷' },
  { code: 'ES', name: 'Espagne', dialCode: '+34', flag: '🇪🇸' },
  { code: 'BE', name: 'Belgique', dialCode: '+32', flag: '🇧🇪' },
  { code: 'CA', name: 'Canada', dialCode: '+1', flag: '🇨🇦' },
  { code: 'US', name: 'États-Unis', dialCode: '+1', flag: '🇺🇸' },
  { code: 'GB', name: 'Royaume-Uni', dialCode: '+44', flag: '🇬🇧' },
  { code: 'DE', name: 'Allemagne', dialCode: '+49', flag: '🇩🇪' },
  { code: 'IT', name: 'Italie', dialCode: '+39', flag: '🇮🇹' },
  { code: 'NL', name: 'Pays-Bas', dialCode: '+31', flag: '🇳🇱' },
  { code: 'PT', name: 'Portugal', dialCode: '+351', flag: '🇵🇹' },
  { code: 'SA', name: 'Arabie Saoudite', dialCode: '+966', flag: '🇸🇦' },
  { code: 'AE', name: 'Émirats Arabes Unis', dialCode: '+971', flag: '🇦🇪' },
  { code: 'EG', name: 'Égypte', dialCode: '+20', flag: '🇪🇬' },
  { code: 'SN', name: 'Sénégal', dialCode: '+221', flag: '🇸🇳' },
  { code: 'CI', name: 'Côte d\'Ivoire', dialCode: '+225', flag: '🇨🇮' },
  { code: 'ML', name: 'Mali', dialCode: '+223', flag: '🇲🇱' },
  { code: 'MR', name: 'Mauritanie', dialCode: '+222', flag: '🇲🇷' },
];

// Emergency contact relationships
// Function to create Zod Schema for profile validation based on user role
const createProfileSchema = (isStaff: boolean, canAlsoBeProfesor: boolean = false, isAcademicCoordinator: boolean = false) => z.object({
  // Identity
  email: z.string().email('Email invalide'),
  cnie: z.string().optional(),
  passport: z.string().optional(),
  nom: z.string().min(2, 'Nom requis (min 2 caractères)'),
  prenom: z.string().min(2, 'Prénom requis (min 2 caractères)'),
  // Arabic names are optional (not all users may have Arabic equivalents)
  nomArabe: z.string().optional(),
  prenomArabe: z.string().optional(),

  // Personal info
  phoneCountry: z.string().optional(),
  // Phone is required for staff
  telephone: isStaff
    ? z.string().min(1, 'Le numéro de téléphone est obligatoire')
    : z.string().optional(),
  sexe: z.enum(['M', 'F'] as const),
  dateNaissance: z.string().refine(
    (val) => {
      if (!val) return false;
      const age = calculateAge(val);
      return age >= 16 && age <= 90;
    },
    { message: 'Âge doit être entre 16 et 90 ans' }
  ),
  paysOrigine: z.string().min(2, 'Pays requis'),
  handicap: z.enum(['N', 'A', 'M', 'V', 'AM', 'AV', 'MV', 'T', 'XX'] as const),

  // Address - required for staff
  adresse: isStaff
    ? z.string().min(1, "L'adresse est obligatoire")
    : z.string().optional(),

  // Emergency contact - all required for staff
  urgenceNom: isStaff
    ? z.string().min(1, "Le nom du contact d'urgence est obligatoire")
    : z.string().optional(),
  urgenceRelation: isStaff
    ? z.string().min(1, "La relation avec le contact d'urgence est obligatoire")
    : z.string().optional(),
  urgencePhoneCountry: z.string().optional(),
  urgenceNumero: isStaff
    ? z.string().min(1, "Le numéro du contact d'urgence est obligatoire")
    : z.string().optional(),
  urgenceAdresse: isStaff
    ? z.string().min(1, "L'adresse du contact d'urgence est obligatoire")
    : z.string().optional(),

  // Staff-specific fields (professional info)
  // RIB is required for staff (with validation)
  rib: isStaff
    ? z.string().min(1, 'Le RIB bancaire est obligatoire').refine(
        (val) => validateRIB(val || ''),
        { message: 'Le RIB doit contenir exactement 24 chiffres' }
      )
    : z.string().optional().refine(
        (val) => !val || validateRIB(val),
        { message: 'Le RIB doit contenir exactement 24 chiffres' }
      ),
  // Salary - View-only for staff (except DG), filled by admin who creates the user
  salaireMensuelNet: z.string().optional(),
  // Function - View-only for staff (except DG), filled by admin who creates the user
  fonction: z.string().optional(),
  // Diploma is required for staff (can be edited by staff)
  diplomeId: isStaff
    ? z.string().min(1, 'Le diplôme est obligatoire')
    : z.string().optional(),
  // Recruitment date - View-only for staff (except DG), filled by admin who creates the user
  dateRecrutement: z.string().optional(),

  // Professor E-DEP fields
  statutProfesseur: z.enum(['P', 'V'] as const).optional(),
  departementAttacheId: z.string().optional(),
  estOriginaireEtablissementPublic: z.enum(['O', 'N'] as const).optional(),
  etablissementOrigine: z.string().optional(),
  gradeId: z.string().optional(),
  specialite: z.string().optional(),
  matieresEnseignees: z.string().optional(),
  tauxHoraire: z.string().optional(),

  // Staff who also teach - required for staff roles that can teach (except Academic Coordinators who can't change it)
  isAlsoProfessor: canAlsoBeProfesor && !isAcademicCoordinator
    ? z.boolean({ required_error: 'Veuillez indiquer si vous êtes également professeur/enseignant' })
    : z.boolean().optional(),

  // Payroll campus - optional for all (assigned by admin or self for DG)
  payrollCampusId: z.string().optional(),
}).superRefine((data, ctx) => {
  // Identity rule (ministry file): Moroccans give their CNIE; everyone else must
  // give a passport number (and upload the passport scan). A CNIE / residence
  // card may be added by non-Moroccans but never replaces the passport.
  if (data.paysOrigine === 'MA' && !data.cnie) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['cnie'], message: 'Numéro CNIE requis pour les marocains' });
  }
  if (data.paysOrigine !== 'MA' && !data.passport) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['passport'],
      message: 'Numéro de passeport requis pour les non-marocains (le scan du passeport est aussi obligatoire)',
    });
  }
});

// Default schema for non-staff (used for type inference)
const profileSchema = createProfileSchema(false);

type ProfileFormData = z.infer<typeof profileSchema>;

// Extended profile fields including staff-specific data
export interface ExtendedProfileFields extends CommonProfileFields {
  rib?: string;
  salaireMensuelNet?: string;
  fonction?: string;
  diplomeId?: string;
  dateRecrutement?: string;
  // Professor E-DEP fields
  statutProfesseur?: 'P' | 'V';
  departementAttacheId?: string;
  estOriginaireEtablissementPublic?: 'O' | 'N';
  etablissementOrigine?: string;
  gradeId?: string;
  specialite?: string;
  matieresEnseignees?: string;
  tauxHoraire?: string;
  // Staff who also teach
  isAlsoProfessor?: boolean;
  // Payroll campus
  payrollCampusId?: string;
}

interface ProfileFormProps {
  initialData?: Partial<ExtendedProfileFields>;
  onSubmit: (data: ExtendedProfileFields) => void;
  onSaveProgress?: (data: Partial<ExtendedProfileFields>) => void;
  onCancel?: () => void;
  isLoading?: boolean;
  isSavingProgress?: boolean;
  progressSaved?: boolean;
  userRole?: string;
  onCountryChange?: (countryCode: string) => void;
  showActions?: boolean;
  authToken?: string; // For authenticated API calls (departements)
  departements?: NomenclatureDepartement[]; // Pre-loaded departements
  campuses?: Campus[]; // Pre-loaded campuses for payroll campus selection
  // Callback to notify parent when professor E-DEP fields change (for real-time document display)
  onProfessorFieldsChange?: (statutProfesseur?: string, estOriginaireEtablissementPublic?: string) => void;
  // Callback to notify parent when isAlsoProfessor changes (for real-time document display)
  onIsAlsoProfessorChange?: (value: boolean) => void;
}

// Ref handle type for external access
export interface ProfileFormHandle {
  getFormValues: () => Partial<CommonProfileFields>;
  submitForm: () => void;
}

// Phone country selector component - Uses portal to avoid clipping by overflow:hidden parents
const PhoneCountrySelector: React.FC<{
  value: string;
  onChange: (code: string) => void;
}> = ({ value, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  const selected = COUNTRY_PHONE_CODES.find(c => c.code === value) || COUNTRY_PHONE_CODES[0];
  const filtered = COUNTRY_PHONE_CODES.filter(c =>
    c.name.toLowerCase().includes(search.toLowerCase()) ||
    c.dialCode.includes(search)
  );

  // Update dropdown position when opened
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setPosition({
        top: rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
      });
    }
  }, [isOpen]);

  // Close on click outside
  useEffect(() => {
    if (!isOpen) return undefined;

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        dropdownRef.current && !dropdownRef.current.contains(target) &&
        buttonRef.current && !buttonRef.current.contains(target)
      ) {
        setIsOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1 px-2 py-2 border border-gray-300 rounded-l-lg bg-gray-50 hover:bg-gray-100 min-w-[90px]"
      >
        <span className="text-lg">{selected.flag}</span>
        <span className="text-sm text-gray-600">{selected.dialCode}</span>
        <ChevronDown className="w-3 h-3 text-gray-400" />
      </button>

      {isOpen && ReactDOM.createPortal(
        <div
          ref={dropdownRef}
          className="fixed z-[9999] w-64 bg-white border border-gray-200 rounded-lg shadow-lg max-h-72 overflow-hidden"
          style={{ top: position.top, left: position.left }}
        >
          <div className="p-2 border-b">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Rechercher..."
                className="w-full pl-8 pr-2 py-1.5 text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-primary-500"
                autoFocus
              />
            </div>
          </div>
          <div className="overflow-y-auto max-h-56">
            {filtered.map(country => (
              <button
                key={country.code}
                type="button"
                onClick={() => {
                  onChange(country.code);
                  setIsOpen(false);
                  setSearch('');
                }}
                className={`w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-gray-50 ${
                  value === country.code ? 'bg-primary-50' : ''
                }`}
              >
                <span className="text-lg">{country.flag}</span>
                <span className="flex-1 text-sm">{country.name}</span>
                <span className="text-xs text-gray-500">{country.dialCode}</span>
                {value === country.code && <Check className="w-4 h-4 text-primary-600" />}
              </button>
            ))}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

// Virtual Arabic Keyboard Component - Uses fixed positioning to avoid clipping
const VirtualArabicKeyboard: React.FC<{
  onKeyPress: (char: string) => void;
  onBackspace: () => void;
  onClose: () => void;
  anchorRef: React.RefObject<HTMLDivElement>;
}> = ({ onKeyPress, onBackspace, onClose, anchorRef }) => {
  const [position, setPosition] = useState<{ top: number; left: number; width: number }>({ top: 0, left: 0, width: 300 });

  useEffect(() => {
    const updatePosition = () => {
      if (anchorRef.current) {
        const rect = anchorRef.current.getBoundingClientRect();
        const keyboardHeight = 180; // Approximate keyboard height
        const viewportHeight = window.innerHeight;

        // Check if there's enough space below, otherwise show above
        const showAbove = rect.bottom + keyboardHeight > viewportHeight && rect.top > keyboardHeight;

        setPosition({
          top: showAbove ? rect.top - keyboardHeight - 5 : rect.bottom + 5,
          left: rect.left,
          width: Math.max(rect.width, 280),
        });
      }
    };

    updatePosition();
    window.addEventListener('scroll', updatePosition);
    window.addEventListener('resize', updatePosition);

    return () => {
      window.removeEventListener('scroll', updatePosition);
      window.removeEventListener('resize', updatePosition);
    };
  }, [anchorRef]);

  return ReactDOM.createPortal(
    <div
      data-arabic-keyboard
      className="fixed z-[9999] p-2 bg-white border border-gray-300 rounded-lg shadow-xl"
      style={{ top: position.top, left: position.left, width: position.width }}
    >
      <div className="flex justify-between items-center mb-2 pb-2 border-b border-gray-200">
        <span className="text-xs text-gray-500 font-medium">لوحة المفاتيح العربية</span>
        <button
          type="button"
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 p-1"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      <div className="space-y-1">
        {ARABIC_KEYBOARD_LAYOUT.map((row, rowIndex) => (
          <div key={rowIndex} className="flex justify-center gap-0.5">
            {row.map((char, charIndex) => (
              <button
                key={charIndex}
                type="button"
                onClick={() => onKeyPress(char)}
                className="w-7 h-8 text-sm font-medium bg-gray-100 hover:bg-primary-100 hover:text-primary-700 rounded transition-colors border border-gray-200"
              >
                {char}
              </button>
            ))}
          </div>
        ))}
        <div className="flex justify-center gap-1 mt-2">
          <button
            type="button"
            onClick={() => onKeyPress(' ')}
            className="flex-1 h-8 text-xs bg-gray-100 hover:bg-gray-200 rounded border border-gray-200"
          >
            espace
          </button>
          <button
            type="button"
            onClick={onBackspace}
            className="w-16 h-8 text-xs bg-red-50 hover:bg-red-100 text-red-600 rounded border border-red-200"
          >
            ← effacer
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

// Calendar Portal Component - Renders calendar in a portal to avoid overflow clipping
const CalendarPortal: React.FC<{
  anchorRef: React.RefObject<HTMLDivElement>;
  viewYear: number;
  viewMonth: number;
  setViewYear: (year: number) => void;
  setViewMonth: (month: number) => void;
  years: number[];
  monthNames: string[];
  renderCalendarDays: () => (number | null)[];
  isDateDisabled: (day: number) => boolean;
  isSelected: (day: number) => boolean;
  onDateSelect: (day: number) => void;
  onClose: () => void;
}> = ({
  anchorRef,
  viewYear,
  viewMonth,
  setViewYear,
  setViewMonth,
  years,
  monthNames,
  renderCalendarDays,
  isDateDisabled,
  isSelected,
  onDateSelect,
}) => {
  const [position, setPosition] = useState<{ top: number; left: number }>({ top: 0, left: 0 });

  useEffect(() => {
    const updatePosition = () => {
      if (anchorRef.current) {
        const rect = anchorRef.current.getBoundingClientRect();
        const calendarHeight = 320; // Approximate calendar height
        const viewportHeight = window.innerHeight;

        // Check if there's enough space below, otherwise show above
        const showAbove = rect.bottom + calendarHeight > viewportHeight && rect.top > calendarHeight;

        // For fixed positioning, use viewport-relative coordinates (no scroll offset needed)
        setPosition({
          top: showAbove ? rect.top - calendarHeight - 5 : rect.bottom + 5,
          left: rect.left,
        });
      }
    };

    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);

    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [anchorRef]);

  return ReactDOM.createPortal(
    <div
      data-calendar-portal
      className="fixed z-[9999] bg-white border border-gray-300 rounded-lg shadow-xl p-3 w-80"
      style={{ top: `${position.top}px`, left: `${position.left}px` }}
    >
      {/* Year and Month selectors */}
      <div className="flex gap-2 mb-3">
        <select
          value={viewYear}
          onChange={(e) => setViewYear(parseInt(e.target.value))}
          className="flex-1 px-2 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
        >
          {years.map(year => (
            <option key={year} value={year}>{year}</option>
          ))}
        </select>
        <select
          value={viewMonth}
          onChange={(e) => setViewMonth(parseInt(e.target.value))}
          className="flex-1 px-2 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
        >
          {monthNames.map((name, index) => (
            <option key={index} value={index}>{name}</option>
          ))}
        </select>
      </div>

      {/* Days of week header */}
      <div className="grid grid-cols-7 gap-1 mb-1">
        {['Di', 'Lu', 'Ma', 'Me', 'Je', 'Ve', 'Sa'].map(day => (
          <div key={day} className="text-center text-xs font-medium text-gray-500 py-1">
            {day}
          </div>
        ))}
      </div>

      {/* Calendar days */}
      <div className="grid grid-cols-7 gap-1">
        {renderCalendarDays().map((day, index) => (
          <div key={index} className="aspect-square">
            {day !== null && (
              <button
                type="button"
                onClick={() => onDateSelect(day)}
                disabled={isDateDisabled(day)}
                className={`w-full h-full flex items-center justify-center text-sm rounded transition-colors ${
                  isSelected(day)
                    ? 'bg-primary-600 text-white'
                    : isDateDisabled(day)
                    ? 'text-gray-300 cursor-not-allowed'
                    : 'hover:bg-primary-50 text-gray-700'
                }`}
              >
                {day}
              </button>
            )}
          </div>
        ))}
      </div>
    </div>,
    document.body
  );
};

// Date picker component with calendar popup and Year/Month dropdowns
// mode: 'birthdate' = for selecting birth dates (constraints are ages), 'date' = for any date (constraints are years)
const DatePicker: React.FC<{
  value: string;
  onChange: (value: string) => void;
  onBlur: () => void;
  error?: string;
  minAge?: number;
  maxAge?: number;
  mode?: 'birthdate' | 'date';
  minYear?: number;
  maxYear?: number;
}> = ({ value, onChange, onBlur, error, minAge = 16, maxAge = 90, mode = 'birthdate', minYear, maxYear }) => {
  const [showCalendar, setShowCalendar] = useState(false);
  const [inputValue, setInputValue] = useState(toDisplayDate(value) || value || '');
  const containerRef = useRef<HTMLDivElement>(null);

  // Calculate min and max dates based on mode
  const today = new Date();
  let maxDate: Date;
  let minDate: Date;

  if (mode === 'date') {
    // For general date picker (like recruitment date)
    maxDate = maxYear ? new Date(maxYear, 11, 31) : today; // Default max is today
    minDate = minYear ? new Date(minYear, 0, 1) : new Date(today.getFullYear() - 50, 0, 1); // Default min is 50 years ago
  } else {
    // For birth date picker (age-based constraints)
    maxDate = new Date(today.getFullYear() - minAge, today.getMonth(), today.getDate());
    minDate = new Date(today.getFullYear() - maxAge, today.getMonth(), today.getDate());
  }

  // Current calendar view month/year
  const [viewYear, setViewYear] = useState(() => {
    const iso = toIsoDate(value);
    if (iso) return parseInt(iso.slice(0, 4), 10);
    // Default view: for dates mode show current year, for birthdate show 20 years ago
    return mode === 'date' ? today.getFullYear() : today.getFullYear() - 20;
  });

  const [viewMonth, setViewMonth] = useState(() => {
    const iso = toIsoDate(value);
    if (iso) return parseInt(iso.slice(5, 7), 10) - 1;
    return 0; // January
  });

  // Generate year options (descending order)
  const years = Array.from(
    { length: maxDate.getFullYear() - minDate.getFullYear() + 1 },
    (_, i) => maxDate.getFullYear() - i
  );

  // Month names in French
  const monthNames = [
    'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
    'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
  ];

  // Close calendar when clicking outside (check both container and portal)
  useEffect(() => {
    if (!showCalendar) return; // Only add listener when calendar is open

    const handleClickOutside = (e: MouseEvent) => {
      const calendarPortal = document.querySelector('[data-calendar-portal]');
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node) &&
        (!calendarPortal || !calendarPortal.contains(e.target as Node))
      ) {
        setShowCalendar(false);
      }
    };

    // Use setTimeout to avoid closing immediately after opening
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 0);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showCalendar]);

  // Sync input value with prop
  useEffect(() => {
    setInputValue(toDisplayDate(value) || value || '');
    // Update view month/year when value changes
    const iso = toIsoDate(value);
    if (iso) {
      setViewYear(parseInt(iso.slice(0, 4), 10));
      setViewMonth(parseInt(iso.slice(5, 7), 10) - 1);
    }
  }, [value]);

  // Format input as user types (auto-add slashes, enforce DD/MM/YYYY)
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let val = e.target.value.replace(/[^\d/]/g, ''); // Only allow digits and slashes

    // Remove extra slashes
    const slashCount = (val.match(/\//g) || []).length;
    if (slashCount > 2) {
      val = val.replace(/\/([^/]*)$/, '$1');
    }

    // Auto-format: add slashes at positions 2 and 5
    const digits = val.replace(/\//g, '');
    if (digits.length >= 2) {
      val = digits.slice(0, 2) + '/' + digits.slice(2);
    }
    if (digits.length >= 4) {
      val = digits.slice(0, 2) + '/' + digits.slice(2, 4) + '/' + digits.slice(4);
    }

    // Limit to 10 characters (DD/MM/YYYY)
    val = val.slice(0, 10);

    setInputValue(val);

    // Validate and update parent if complete and valid
    if (val.length === 10) {
      const parts = val.split('/');
      if (parts.length === 3) {
        const [dayStr, monthStr, yearStr] = parts;
        const day = parseInt(dayStr);
        const month = parseInt(monthStr);
        const year = parseInt(yearStr);

        if (isValidDate(day, month, year)) {
          // Format with leading zeros
          const formatted = `${String(day).padStart(2, '0')}/${String(month).padStart(2, '0')}/${year}`;
          setInputValue(formatted);
          onChange(toIsoDate(formatted)); // the form and the API only ever see YYYY-MM-DD
        }
      }
    }
  };

  const isValidDate = (day: number, month: number, year: number): boolean => {
    if (month < 1 || month > 12) return false;
    if (day < 1 || day > 31) return false;
    if (year < minDate.getFullYear() || year > maxDate.getFullYear()) return false;

    // Check days in month
    const daysInMonth = new Date(year, month, 0).getDate();
    if (day > daysInMonth) return false;

    // Check age constraints
    const date = new Date(year, month - 1, day);
    return date >= minDate && date <= maxDate;
  };

  const handleDateSelect = (day: number) => {
    const formatted = `${String(day).padStart(2, '0')}/${String(viewMonth + 1).padStart(2, '0')}/${viewYear}`;
    setInputValue(formatted);
    onChange(toIsoDate(formatted));
    setShowCalendar(false);
  };

  const getDaysInMonth = (year: number, month: number): number => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (year: number, month: number): number => {
    return new Date(year, month, 1).getDay();
  };

  const renderCalendarDays = () => {
    const daysInMonth = getDaysInMonth(viewYear, viewMonth);
    const firstDay = getFirstDayOfMonth(viewYear, viewMonth);
    const days: (number | null)[] = [];

    // Empty cells before first day
    for (let i = 0; i < firstDay; i++) {
      days.push(null);
    }

    // Days of the month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(i);
    }

    return days;
  };

  const isDateDisabled = (day: number): boolean => {
    const date = new Date(viewYear, viewMonth, day);
    return date < minDate || date > maxDate;
  };

  const isSelected = (day: number): boolean => {
    if (!inputValue || inputValue.length !== 10) return false;
    const parts = inputValue.split('/');
    if (parts.length !== 3) return false;
    return (
      parseInt(parts[0]) === day &&
      parseInt(parts[1]) === viewMonth + 1 &&
      parseInt(parts[2]) === viewYear
    );
  };

  return (
    <div className="relative" ref={containerRef}>
      <div className="relative">
        <input
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onBlur={onBlur}
          className={`w-full px-4 py-2 pr-10 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
            error ? 'border-red-300' : 'border-gray-300'
          }`}
          placeholder="JJ/MM/AAAA"
          maxLength={10}
        />
        <button
          type="button"
          onClick={() => setShowCalendar(!showCalendar)}
          className={`absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded transition-colors ${
            showCalendar ? 'text-primary-600 bg-primary-50' : 'text-gray-400 hover:text-gray-600'
          }`}
          title="Ouvrir le calendrier"
        >
          <Calendar className="w-5 h-5" />
        </button>
      </div>

      {showCalendar && (
        <CalendarPortal
          anchorRef={containerRef}
          viewYear={viewYear}
          viewMonth={viewMonth}
          setViewYear={setViewYear}
          setViewMonth={setViewMonth}
          years={years}
          monthNames={monthNames}
          renderCalendarDays={renderCalendarDays}
          isDateDisabled={isDateDisabled}
          isSelected={isSelected}
          onDateSelect={handleDateSelect}
          onClose={() => setShowCalendar(false)}
        />
      )}

      <p className="text-xs text-gray-500 mt-1">Format: JJ/MM/AAAA (ex: 15/03/1990)</p>
    </div>
  );
};

// Arabic input component with virtual keyboard popup
const ArabicInput: React.FC<{
  value: string;
  onChange: (value: string) => void;
  onBlur: () => void;
  placeholder: string;
  error?: string;
}> = ({ value, onChange, onBlur, placeholder, error }) => {
  const [showKeyboard, setShowKeyboard] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close keyboard when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      // Check if click is outside both the container and the keyboard portal
      const keyboardElement = document.querySelector('[data-arabic-keyboard]');
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node) &&
        (!keyboardElement || !keyboardElement.contains(e.target as Node))
      ) {
        setShowKeyboard(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleKeyPress = (char: string) => {
    const input = inputRef.current;
    if (input) {
      const start = input.selectionStart || value.length;
      const end = input.selectionEnd || value.length;
      const newValue = value.slice(0, start) + char + value.slice(end);
      onChange(newValue);
      // Set cursor position after the inserted character
      setTimeout(() => {
        const newPos = start + char.length;
        input.setSelectionRange(newPos, newPos);
        input.focus();
      }, 0);
    } else {
      onChange(value + char);
    }
  };

  const handleBackspace = () => {
    const input = inputRef.current;
    if (input && value.length > 0) {
      const start = input.selectionStart || value.length;
      const end = input.selectionEnd || value.length;
      if (start === end && start > 0) {
        // No selection, delete character before cursor
        const newValue = value.slice(0, start - 1) + value.slice(end);
        onChange(newValue);
        setTimeout(() => {
          input.setSelectionRange(start - 1, start - 1);
          input.focus();
        }, 0);
      } else if (start !== end) {
        // Selection exists, delete selection
        const newValue = value.slice(0, start) + value.slice(end);
        onChange(newValue);
        setTimeout(() => {
          input.setSelectionRange(start, start);
          input.focus();
        }, 0);
      }
    } else if (value.length > 0) {
      onChange(value.slice(0, -1));
    }
  };

  return (
    <div className="relative" ref={containerRef}>
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={() => {
          // Delay onBlur to allow keyboard clicks
          setTimeout(onBlur, 100);
        }}
        dir="rtl"
        className={`w-full px-4 py-2 pr-10 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-right ${
          error ? 'border-red-300' : 'border-gray-300'
        }`}
        placeholder={placeholder}
      />
      <button
        type="button"
        onClick={() => {
          setShowKeyboard(!showKeyboard);
          if (!showKeyboard) {
            inputRef.current?.focus();
          }
        }}
        className={`absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded transition-colors ${
          showKeyboard ? 'bg-primary-100 text-primary-600' : 'text-gray-400 hover:text-gray-600'
        }`}
        title={showKeyboard ? 'Fermer le clavier' : 'Ouvrir le clavier arabe'}
      >
        <Keyboard className="w-4 h-4" />
      </button>

      {showKeyboard && (
        <VirtualArabicKeyboard
          onKeyPress={handleKeyPress}
          onBackspace={handleBackspace}
          onClose={() => setShowKeyboard(false)}
          anchorRef={containerRef}
        />
      )}
    </div>
  );
};

export const ProfileForm = forwardRef<ProfileFormHandle, ProfileFormProps>(({
  initialData,
  onSubmit,
  onSaveProgress: _onSaveProgress,
  isLoading: _isLoading = false,
  isSavingProgress: _isSavingProgress = false,
  progressSaved: _progressSaved = false,
  userRole = '',
  onCountryChange,
  showActions: _showActions = true,
  authToken,
  departements: propDepartements,
  campuses: propCampuses,
  onProfessorFieldsChange,
  onIsAlsoProfessorChange,
}, ref) => {
  // Determine if user is staff (not student/parent)
  const isStaff = STAFF_ROLES.includes(userRole);
  const isProfesseur = userRole === 'TEACHER';
  // Determine if user can also be a professor (staff roles who can teach, excluding STUDENT/PARENT)
  const canAlsoBeProfesor = STAFF_ROLES_WHO_CAN_TEACH.includes(userRole);
  // DG can edit fields that are read-only for other staff
  const isDG = userRole === 'DIRECTOR_GENERAL';
  // Staff (except DG) should have certain fields in view-only mode (filled by admin who creates the user)
  const isStaffViewOnly = isStaff && !isDG;
  // Academic coordinators cannot change their isAlsoProfessor status (set by creator)
  const isAcademicCoordinator = userRole === 'ACADEMIC_COORDINATOR';

  // Create dynamic validation schema based on user role
  const dynamicSchema = useMemo(() => createProfileSchema(isStaff, canAlsoBeProfesor, isAcademicCoordinator), [isStaff, canAlsoBeProfesor, isAcademicCoordinator]);

  const [expandedSections, setExpandedSections] = useState({
    identity: true,
    personal: true,
    professional: true, // Staff professional section
    professeur: true, // Professor-specific section
    alsoProf: true, // "Are you also a professor?" section
  });
  // Initialize with local data immediately, then try to fetch from API
  const [countries, setCountries] = useState<NomenclatureCountry[]>(() => getCountriesSync());
  const [diplomes, setDiplomes] = useState<NomenclatureDiplome[]>([]);
  const [grades, setGrades] = useState<NomenclatureGrade[]>([]);
  const [departements, setDepartements] = useState<NomenclatureDepartement[]>(propDepartements || []);
  const [campuses, setCampuses] = useState<Campus[]>(propCampuses || []);
  const [diplomeSearch, setDiplomeSearch] = useState('');
  const [showDiplomeDropdown, setShowDiplomeDropdown] = useState(false);
  const [gradeSearch, setGradeSearch] = useState('');
  const [showGradeDropdown, setShowGradeDropdown] = useState(false);
  const [deptSearch, setDeptSearch] = useState('');
  const [showDeptDropdown, setShowDeptDropdown] = useState(false);
  const diplomeDropdownRef = useRef<HTMLDivElement>(null);
  const gradeDropdownRef = useRef<HTMLDivElement>(null);
  const deptDropdownRef = useRef<HTMLDivElement>(null);

  // Optionally try to load countries from API (local data is already available)
  useEffect(() => {
    const loadCountries = async () => {
      try {
        // Try to fetch fresh data from API
        const data = await fetchCountries();
        if (data && data.length > 0) {
          setCountries(data);
        }
      } catch (error) {
        console.error('Error loading countries from API:', error);
        // Keep using local data (already set)
      }
    };
    loadCountries();
  }, []);

  // Load diplomes and grades for staff users
  useEffect(() => {
    if (isStaff) {
      const loadDiplomes = async () => {
        try {
          const data = await fetchDiplomes();
          if (data && data.length > 0) {
            setDiplomes(data);
          }
        } catch (error) {
          console.error('Error loading diplomes from API:', error);
        }
      };
      loadDiplomes();
    }
  }, [isStaff]);

  // Load grades for professors OR staff who also teach
  useEffect(() => {
    // We need to load grades whenever someone could potentially be a professor
    // This includes TEACHER role OR staff roles who can teach (when they select isAlsoProfessor)
    if (isProfesseur || canAlsoBeProfesor) {
      const loadGrades = async () => {
        try {
          const data = await fetchGrades();
          if (data && data.length > 0) {
            setGrades(data);
          }
        } catch (error) {
          console.error('Error loading grades from API:', error);
        }
      };
      loadGrades();
    }
  }, [isProfesseur, canAlsoBeProfesor]);

  // Update departements when prop changes
  useEffect(() => {
    if (propDepartements && propDepartements.length > 0) {
      setDepartements(propDepartements);
    }
  }, [propDepartements]);

  // Update campuses when prop changes
  useEffect(() => {
    if (propCampuses && propCampuses.length > 0) {
      setCampuses(propCampuses);
    }
  }, [propCampuses]);

  // Load campuses for DG if not provided via props
  useEffect(() => {
    if (isDG && campuses.length === 0 && authToken) {
      const loadCampuses = async () => {
        try {
          const data = await fetchCampuses(authToken, true);
          if (data && data.length > 0) {
            setCampuses(data);
          }
        } catch (error) {
          console.error('Error loading campuses from API:', error);
        }
      };
      loadCampuses();
    }
  }, [isDG, campuses.length, authToken]);

  // Load departements for professors OR staff who also teach
  useEffect(() => {
    if ((isProfesseur || canAlsoBeProfesor) && departements.length === 0 && authToken) {
      const loadDepartements = async () => {
        try {
          const data = await fetchDepartements(authToken);
          if (data && data.length > 0) {
            setDepartements(data);
          }
        } catch (error) {
          console.error('Error loading departements from API:', error);
        }
      };
      loadDepartements();
    }
  }, [isProfesseur, canAlsoBeProfesor, departements.length, authToken]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (diplomeDropdownRef.current && !diplomeDropdownRef.current.contains(e.target as Node)) {
        setShowDiplomeDropdown(false);
      }
      if (gradeDropdownRef.current && !gradeDropdownRef.current.contains(e.target as Node)) {
        setShowGradeDropdown(false);
      }
      if (deptDropdownRef.current && !deptDropdownRef.current.contains(e.target as Node)) {
        setShowDeptDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const {
    register,
    control,
    handleSubmit,
    watch,
    setValue,
    getValues,
    reset,
    formState: { errors },
  } = useForm<ProfileFormData>({
    resolver: zodResolver(dynamicSchema),
    defaultValues: {
      email: initialData?.email || '',
      cnie: initialData?.cnie || '',
      passport: initialData?.passport || '',
      nom: initialData?.nom || '',
      prenom: initialData?.prenom || '',
      nomArabe: initialData?.nomArabe || '',
      prenomArabe: initialData?.prenomArabe || '',
      phoneCountry: initialData?.phoneCountry || 'MA',
      telephone: initialData?.telephone || '',
      sexe: initialData?.sexe || 'M',
      dateNaissance: initialData?.dateNaissance || '',
      paysOrigine: toCountryCode(initialData?.paysOrigine) || 'MA',
      handicap: initialData?.handicap || 'N',
      adresse: initialData?.adresse || '',
      urgenceNom: initialData?.urgenceNom || '',
      urgenceRelation: initialData?.urgenceRelation || '',
      urgencePhoneCountry: initialData?.urgencePhoneCountry || 'MA',
      urgenceNumero: initialData?.urgenceNumero || '',
      urgenceAdresse: initialData?.urgenceAdresse || '',
      // Staff-specific fields
      rib: initialData?.rib || '',
      salaireMensuelNet: initialData?.salaireMensuelNet || '',
      fonction: initialData?.fonction || '',
      diplomeId: initialData?.diplomeId || '',
      dateRecrutement: initialData?.dateRecrutement || '',
      // Professor E-DEP fields
      statutProfesseur: initialData?.statutProfesseur || undefined,
      departementAttacheId: initialData?.departementAttacheId || '',
      estOriginaireEtablissementPublic: initialData?.estOriginaireEtablissementPublic || undefined,
      etablissementOrigine: initialData?.etablissementOrigine || '',
      gradeId: initialData?.gradeId || '',
      specialite: initialData?.specialite || '',
      matieresEnseignees: initialData?.matieresEnseignees || '',
      tauxHoraire: initialData?.tauxHoraire || '',
      // Staff who also teach - keep undefined if not set to trigger required validation
      isAlsoProfessor: initialData?.isAlsoProfessor,
      // Payroll campus
      payrollCampusId: initialData?.payrollCampusId || '',
    },
  });

  // Track the last data hash we've seen to detect real changes from storage
  const lastDataHashRef = useRef<string>('');

  // Reset form when initialData changes with new meaningful data from storage
  useEffect(() => {
    if (initialData) {
      // Create a comprehensive hash of the data that matters (including staff fields)
      const dataHash = JSON.stringify({
        nom: initialData.nom || '',
        prenom: initialData.prenom || '',
        nomArabe: initialData.nomArabe || '',
        prenomArabe: initialData.prenomArabe || '',
        dateNaissance: initialData.dateNaissance || '',
        cnie: initialData.cnie || '',
        passport: initialData.passport || '',
        telephone: initialData.telephone || '',
        phoneCountry: initialData.phoneCountry || '',
        adresse: initialData.adresse || '',
        sexe: initialData.sexe || '',
        paysOrigine: toCountryCode(initialData.paysOrigine),
        handicap: initialData.handicap || '',
        email: initialData.email || '',
        urgenceNom: initialData.urgenceNom || '',
        urgenceRelation: initialData.urgenceRelation || '',
        urgencePhoneCountry: initialData.urgencePhoneCountry || '',
        urgenceNumero: initialData.urgenceNumero || '',
        urgenceAdresse: initialData.urgenceAdresse || '',
        // Staff-specific fields
        rib: initialData.rib || '',
        salaireMensuelNet: initialData.salaireMensuelNet || '',
        fonction: initialData.fonction || '',
        diplomeId: initialData.diplomeId || '',
        dateRecrutement: initialData.dateRecrutement || '',
        // Professor E-DEP fields
        statutProfesseur: initialData.statutProfesseur || '',
        departementAttacheId: initialData.departementAttacheId || '',
        estOriginaireEtablissementPublic: initialData.estOriginaireEtablissementPublic || '',
        etablissementOrigine: initialData.etablissementOrigine || '',
        gradeId: initialData.gradeId || '',
        specialite: initialData.specialite || '',
        tauxHoraire: initialData.tauxHoraire || '',
        // Staff who also teach
        isAlsoProfessor: initialData.isAlsoProfessor,
        // Payroll campus
        payrollCampusId: initialData.payrollCampusId || '',
      });

      // Only reset if this is genuinely new/different data
      if (dataHash !== lastDataHashRef.current) {
        reset({
          email: initialData.email || '',
          cnie: initialData.cnie || '',
          passport: initialData.passport || '',
          nom: initialData.nom || '',
          prenom: initialData.prenom || '',
          nomArabe: initialData.nomArabe || '',
          prenomArabe: initialData.prenomArabe || '',
          phoneCountry: initialData.phoneCountry || 'MA',
          telephone: initialData.telephone || '',
          sexe: initialData.sexe || 'M',
          dateNaissance: initialData.dateNaissance || '',
          paysOrigine: toCountryCode(initialData.paysOrigine) || 'MA',
          handicap: initialData.handicap || 'N',
          adresse: initialData.adresse || '',
          urgenceNom: initialData.urgenceNom || '',
          urgenceRelation: initialData.urgenceRelation || '',
          urgencePhoneCountry: initialData.urgencePhoneCountry || 'MA',
          urgenceNumero: initialData.urgenceNumero || '',
          urgenceAdresse: initialData.urgenceAdresse || '',
          // Staff-specific fields
          rib: initialData.rib || '',
          salaireMensuelNet: initialData.salaireMensuelNet || '',
          fonction: initialData.fonction || '',
          diplomeId: initialData.diplomeId || '',
          dateRecrutement: initialData.dateRecrutement || '',
          // Professor E-DEP fields
          statutProfesseur: initialData.statutProfesseur || undefined,
          departementAttacheId: initialData.departementAttacheId || '',
          estOriginaireEtablissementPublic: initialData.estOriginaireEtablissementPublic || undefined,
          etablissementOrigine: initialData.etablissementOrigine || '',
          gradeId: initialData.gradeId || '',
          specialite: initialData.specialite || '',
          matieresEnseignees: initialData.matieresEnseignees || '',
          tauxHoraire: initialData.tauxHoraire || '',
          // Staff who also teach
          isAlsoProfessor: initialData.isAlsoProfessor,
          // Payroll campus
          payrollCampusId: initialData.payrollCampusId || '',
        });
        lastDataHashRef.current = dataHash;
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData]);

  const selectedCountry = watch('paysOrigine');
  const isMoroccan = selectedCountry === 'MA';
  const statutProfesseur = watch('statutProfesseur');
  const isVacataire = statutProfesseur === 'V';
  const estOriginaireEtablissementPublic = watch('estOriginaireEtablissementPublic');
  const isAlsoProfessor = watch('isAlsoProfessor');

  // Show professor fields if user is a TEACHER OR if they're a staff member who also teaches
  const showProfessorFields = isProfesseur || (canAlsoBeProfesor && isAlsoProfessor);

  // Notify parent when country changes
  useEffect(() => {
    if (onCountryChange && selectedCountry) {
      onCountryChange(selectedCountry);
    }
  }, [selectedCountry, onCountryChange]);

  // Notify parent when professor E-DEP fields change (for real-time document display)
  // Also notify when isAlsoProfessor changes for staff members who can teach
  useEffect(() => {
    if (onProfessorFieldsChange && showProfessorFields) {
      onProfessorFieldsChange(statutProfesseur, estOriginaireEtablissementPublic);
    }
  }, [statutProfesseur, estOriginaireEtablissementPublic, onProfessorFieldsChange, showProfessorFields]);

  // Auto-set statutProfesseur to 'P' (Permanent) for staff who also teach
  // This ensures the value is set when loading existing data
  useEffect(() => {
    if (isAlsoProfessor === true && !statutProfesseur) {
      setValue('statutProfesseur', 'P');
    }
  }, [isAlsoProfessor, statutProfesseur, setValue]);

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const onFormSubmit = (data: ProfileFormData) => {
    onSubmit(data as unknown as ExtendedProfileFields);
  };

  // A non-Moroccan without a passport number cannot submit yet, but the
  // country they chose must not be lost (people used to switch back to Maroc
  // just to get through): keep it as a draft and say exactly what is missing.
  const keepCountryDraft = (errors: Record<string, unknown>): boolean => {
    const values = getValues();
    const country = String(values.paysOrigine || '');
    if (!country || country === 'MA' || !errors.passport || !_onSaveProgress) return false;
    _onSaveProgress(values as unknown as Partial<ExtendedProfileFields>);
    toast.error(
      "Votre pays d'origine est enregistré. Pour soumettre votre profil, ajoutez votre numéro de passeport et téléversez le scan de votre passeport.",
      { duration: 8000, icon: '🛂' },
    );
    return true;
  };

  // User-friendly error messages for each field
  const getFieldErrorMessage = (field: string): string => {
    const fieldMessages: Record<string, string> = {
      rib: 'Veuillez saisir votre RIB bancaire (24 chiffres exactement)',
      diplomeId: 'Veuillez sélectionner votre diplôme',
      telephone: 'Veuillez saisir votre numéro de téléphone',
      adresse: 'Veuillez saisir votre adresse',
      urgenceNom: 'Veuillez saisir le nom de votre contact d\'urgence',
      urgenceRelation: 'Veuillez sélectionner la relation avec votre contact d\'urgence',
      urgenceNumero: 'Veuillez saisir le numéro de téléphone de votre contact d\'urgence',
      urgenceAdresse: 'Veuillez saisir l\'adresse de votre contact d\'urgence',
      isAlsoProfessor: 'Veuillez indiquer si vous êtes également professeur/enseignant',
      nom: 'Veuillez saisir votre nom (minimum 2 caractères)',
      prenom: 'Veuillez saisir votre prénom (minimum 2 caractères)',
      email: 'Veuillez saisir une adresse email valide',
      sexe: 'Veuillez sélectionner votre sexe',
      dateNaissance: 'Veuillez saisir une date de naissance valide (âge entre 16 et 90 ans)',
      paysOrigine: 'Veuillez sélectionner votre pays d\'origine',
      cnie: 'Veuillez saisir votre numéro CNIE (obligatoire pour les marocains)',
      passport: 'Veuillez saisir votre numéro de passeport et téléverser son scan (obligatoire pour les non-marocains)',
    };
    return fieldMessages[field] || 'Veuillez vérifier ce champ';
  };

  // Expose methods to parent via ref
  useImperativeHandle(ref, () => ({
    getFormValues: () => getValues() as unknown as Partial<CommonProfileFields>,
    submitForm: () => {
      handleSubmit(
        onFormSubmit,
        (errors) => {
          if (keepCountryDraft(errors as Record<string, unknown>)) return;
          // Show user-friendly message for first error
          const firstError = Object.entries(errors)[0];
          if (firstError) {
            const [field] = firstError;
            const message = getFieldErrorMessage(field);
            toast.error(message, {
              duration: 5000,
              icon: '⚠️',
            });
          }
        }
      )();
    },
  }));

  const SectionHeader: React.FC<{
    title: string;
    icon: React.ReactNode;
    section: keyof typeof expandedSections;
  }> = ({ title, icon, section }) => (
    <button
      type="button"
      onClick={() => toggleSection(section)}
      className="w-full flex items-center justify-between py-3 text-left"
    >
      <div className="flex items-center gap-2">
        {icon}
        <span className="font-semibold text-gray-900">{title}</span>
      </div>
      {expandedSections[section] ? (
        <ChevronUp className="w-5 h-5 text-gray-400" />
      ) : (
        <ChevronDown className="w-5 h-5 text-gray-400" />
      )}
    </button>
  );

  return (
    <div className="space-y-6">
      <form onSubmit={handleSubmit(onFormSubmit, (errors) => { keepCountryDraft(errors as Record<string, unknown>); })} className="space-y-6">
        {/* Identity Section */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-4 border-b border-gray-100">
            <SectionHeader
              title="Identité"
              icon={<User className="w-5 h-5 text-primary-600" />}
              section="identity"
            />
          </div>

          {expandedSections.identity && (
            <div className="p-4 space-y-4">
              {/* Names - Latin */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <span className="block text-sm font-medium text-gray-700">
                    Nom<span className="text-red-500 ml-1">*</span>
                  </span>
                  <input
                    type="text"
                    {...register('nom')}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 uppercase"
                    placeholder="NOM DE FAMILLE"
                  />
                  {errors.nom && <p className="text-xs text-red-500">{errors.nom.message}</p>}
                </div>

                <div className="space-y-1">
                  <span className="block text-sm font-medium text-gray-700">
                    Prénom<span className="text-red-500 ml-1">*</span>
                  </span>
                  <input
                    type="text"
                    {...register('prenom')}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Prénom"
                  />
                  {errors.prenom && <p className="text-xs text-red-500">{errors.prenom.message}</p>}
                </div>
              </div>

              {/* Names - Arabic with virtual keyboard (optional) */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <span className="block text-sm font-medium text-gray-700">
                    الاسم العائلي <span className="text-gray-500">/ Nom en arabe</span>
                    <span className="text-gray-400 text-xs ml-1">(optionnel)</span>
                  </span>
                  <Controller
                    name="nomArabe"
                    control={control}
                    render={({ field }) => (
                      <ArabicInput
                        value={field.value || ''}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        placeholder="الاسم العائلي"
                        error={errors.nomArabe?.message}
                      />
                    )}
                  />
                  <p className="text-xs text-gray-500">Cliquez sur l'icône clavier pour ouvrir le clavier arabe</p>
                  {errors.nomArabe && <p className="text-xs text-red-500">{errors.nomArabe.message}</p>}
                </div>

                <div className="space-y-1">
                  <span className="block text-sm font-medium text-gray-700">
                    الاسم الشخصي <span className="text-gray-500">/ Prénom en arabe</span>
                    <span className="text-gray-400 text-xs ml-1">(optionnel)</span>
                  </span>
                  <Controller
                    name="prenomArabe"
                    control={control}
                    render={({ field }) => (
                      <ArabicInput
                        value={field.value || ''}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        placeholder="الاسم الشخصي"
                        error={errors.prenomArabe?.message}
                      />
                    )}
                  />
                  <p className="text-xs text-gray-500">Cliquez sur l'icône clavier pour ouvrir le clavier arabe</p>
                  {errors.prenomArabe && <p className="text-xs text-red-500">{errors.prenomArabe.message}</p>}
                </div>
              </div>

              {/* CIN (for Moroccans) */}
              {isMoroccan && (
                <div className="space-y-1">
                  <span className="block text-sm font-medium text-gray-700">
                    N° CIN<span className="text-red-500 ml-1">*</span>
                  </span>
                  <input
                    type="text"
                    {...register('cnie')}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="AB123456"
                  />
                  {errors.cnie && <p className="text-xs text-red-500">{errors.cnie.message}</p>}
                </div>
              )}

              {/* Passport (for foreigners) */}
              {!isMoroccan && (
                <div className="space-y-1">
                  <span className="block text-sm font-medium text-gray-700">
                    N° Passeport<span className="text-red-500 ml-1">*</span>
                  </span>
                  <input
                    type="text"
                    {...register('passport')}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Numéro de passeport"
                  />
                  {errors.passport && <p className="text-xs text-red-500">{errors.passport.message}</p>}
                </div>
              )}

              {/* Country Selection */}
              <div className="space-y-1">
                <span className="block text-sm font-medium text-gray-700">
                  Pays d'origine<span className="text-red-500 ml-1">*</span>
                </span>
                <Controller
                  name="paysOrigine"
                  control={control}
                  render={({ field }) => (
                    <div className="relative">
                      <select
                        value={field.value}
                        onChange={(e) => {
                          field.onChange(e.target.value);
                        }}
                        onBlur={field.onBlur}
                        className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 appearance-none bg-white cursor-pointer"
                      >
                        {countries.map(country => (
                          <option key={country.code} value={country.code}>
                            {country.nameFr}
                          </option>
                        ))}
                      </select>
                      <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                    </div>
                  )}
                />
                {errors.paysOrigine && <p className="text-xs text-red-500">{errors.paysOrigine.message}</p>}
              </div>
            </div>
          )}
        </div>

        {/* Personal Information Section */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-4 border-b border-gray-100">
            <SectionHeader
              title="Informations personnelles"
              icon={<Calendar className="w-5 h-5 text-primary-600" />}
              section="personal"
            />
          </div>

          {expandedSections.personal && (
            <div className="p-4 space-y-6">
              {/* Basic Personal Info */}
              <div className="space-y-4">
                {/* Gender - First */}
                <div className="space-y-1">
                  <span className="block text-sm font-medium text-gray-700">
                    Sexe<span className="text-red-500 ml-1">*</span>
                  </span>
                  <Controller
                    name="sexe"
                    control={control}
                    render={({ field }) => (
                      <div className="flex gap-4">
                        <label className={`flex-1 flex items-center justify-center gap-2 p-3 border rounded-lg cursor-pointer transition-colors ${
                          field.value === 'M'
                            ? 'border-primary-500 bg-primary-50 text-primary-700'
                            : 'border-gray-300 hover:border-gray-400'
                        }`}>
                          <input
                            type="radio"
                            value="M"
                            checked={field.value === 'M'}
                            onChange={() => field.onChange('M')}
                            className="sr-only"
                          />
                          <span>Masculin</span>
                        </label>
                        <label className={`flex-1 flex items-center justify-center gap-2 p-3 border rounded-lg cursor-pointer transition-colors ${
                          field.value === 'F'
                            ? 'border-primary-500 bg-primary-50 text-primary-700'
                            : 'border-gray-300 hover:border-gray-400'
                        }`}>
                          <input
                            type="radio"
                            value="F"
                            checked={field.value === 'F'}
                            onChange={() => field.onChange('F')}
                            className="sr-only"
                          />
                          <span>Féminin</span>
                        </label>
                      </div>
                    )}
                  />
                  {errors.sexe && <p className="text-xs text-red-500">{errors.sexe.message}</p>}
                </div>

                {/* Email - Second */}
                <div className="space-y-1">
                  <span className="block text-sm font-medium text-gray-700">
                    Email<span className="text-red-500 ml-1">*</span>
                  </span>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="email"
                      {...register('email')}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      placeholder="email@exemple.com"
                    />
                  </div>
                  {errors.email && <p className="text-xs text-red-500">{errors.email.message}</p>}
                </div>

                {/* Phone with country selector - Third */}
                <div className="space-y-1">
                  <span className="block text-sm font-medium text-gray-700">
                    Téléphone{isStaff && <span className="text-red-500 ml-1">*</span>}
                  </span>
                  <div className="flex">
                    <Controller
                      name="phoneCountry"
                      control={control}
                      render={({ field }) => (
                        <PhoneCountrySelector
                          value={field.value || 'MA'}
                          onChange={field.onChange}
                        />
                      )}
                    />
                    <input
                      type="tel"
                      {...register('telephone')}
                      autoComplete="tel"
                      className="flex-1 px-4 py-2 border border-l-0 border-gray-300 rounded-r-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      placeholder="6XX XXX XXX"
                    />
                  </div>
                  {errors.telephone && <p className="text-xs text-red-500">{errors.telephone.message}</p>}
                </div>

                {/* Date of Birth - Fourth */}
                <div className="space-y-1">
                  <span className="block text-sm font-medium text-gray-700">
                    Date de naissance<span className="text-red-500 ml-1">*</span>
                  </span>
                  <Controller
                    name="dateNaissance"
                    control={control}
                    render={({ field }) => (
                      <DatePicker
                        value={field.value}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        error={errors.dateNaissance?.message}
                        minAge={16}
                        maxAge={90}
                      />
                    )}
                  />
                  {errors.dateNaissance && <p className="text-xs text-red-500">{errors.dateNaissance.message}</p>}
                </div>

                {/* Handicap - Fifth */}
                <div className="space-y-1">
                  <span className="block text-sm font-medium text-gray-700">
                    Situation de handicap<span className="text-red-500 ml-1">*</span>
                  </span>
                  <Controller
                    name="handicap"
                    control={control}
                    render={({ field }) => (
                      <select
                        value={field.value}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      >
                        {handicapOptions.map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label} - {option.description}
                          </option>
                        ))}
                      </select>
                    )}
                  />
                  {errors.handicap && <p className="text-xs text-red-500">{errors.handicap.message}</p>}
                </div>
              </div>

              {/* Address Sub-section */}
              <div className="pt-4 border-t border-gray-200">
                <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-gray-500" />
                  Adresse
                </h4>
                <div className="space-y-4">
                  <div className="space-y-1">
                    <span className="block text-sm font-medium text-gray-700">
                      Adresse complète{isStaff && <span className="text-red-500 ml-1">*</span>}
                    </span>
                    <textarea
                      {...register('adresse')}
                      rows={2}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      placeholder="Numéro, rue, quartier..."
                    />
                    {errors.adresse && <p className="text-xs text-red-500">{errors.adresse.message}</p>}
                  </div>

                </div>
              </div>

              {/* Emergency Contact Sub-section */}
              <div className="pt-4 border-t border-gray-200">
                <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-500" />
                  Contact d'urgence
                </h4>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <span className="block text-sm font-medium text-gray-700">
                        Nom du contact{isStaff && <span className="text-red-500 ml-1">*</span>}
                      </span>
                      <input
                        type="text"
                        {...register('urgenceNom')}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        placeholder="Nom complet"
                      />
                      {errors.urgenceNom && <p className="text-xs text-red-500">{errors.urgenceNom.message}</p>}
                    </div>

                    <div className="space-y-1">
                      <span className="block text-sm font-medium text-gray-700">
                        Relation{isStaff && <span className="text-red-500 ml-1">*</span>}
                      </span>
                      <select
                        {...register('urgenceRelation')}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      >
                        {emergencyRelationSelectOptions(initialData?.urgenceRelation).map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Emergency phone with country selector */}
                  <div className="space-y-1">
                    <span className="block text-sm font-medium text-gray-700">
                      Numéro de téléphone{isStaff && <span className="text-red-500 ml-1">*</span>}
                    </span>
                    <div className="flex">
                      <Controller
                        name="urgencePhoneCountry"
                        control={control}
                        render={({ field }) => (
                          <PhoneCountrySelector
                            value={field.value || 'MA'}
                            onChange={field.onChange}
                          />
                        )}
                      />
                      <input
                        type="tel"
                        {...register('urgenceNumero')}
                        autoComplete="tel"
                        className="flex-1 px-4 py-2 border border-l-0 border-gray-300 rounded-r-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        placeholder="6XX XXX XXX"
                      />
                    </div>
                    {errors.urgenceNumero && <p className="text-xs text-red-500">{errors.urgenceNumero.message}</p>}
                  </div>

                  <div className="space-y-1">
                    <span className="block text-sm font-medium text-gray-700">
                      Adresse du contact{isStaff && <span className="text-red-500 ml-1">*</span>}
                    </span>
                    <input
                      type="text"
                      {...register('urgenceAdresse')}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      placeholder="Adresse complète"
                    />
                    {errors.urgenceAdresse && <p className="text-xs text-red-500">{errors.urgenceAdresse.message}</p>}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Professional Information Section - Staff Only */}
        {isStaff && (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="px-4 border-b border-gray-100">
              <button
                type="button"
                onClick={() => toggleSection('professional')}
                className="w-full flex items-center justify-between py-3 text-left"
              >
                <div className="flex items-center gap-2">
                  <Briefcase className="w-5 h-5 text-primary-600" />
                  <span className="font-semibold text-gray-900">Informations professionnelles</span>
                </div>
                {expandedSections.professional ? (
                  <ChevronUp className="w-5 h-5 text-gray-400" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-gray-400" />
                )}
              </button>
            </div>

            {expandedSections.professional && (
              <div className="p-4 space-y-4">
                {/* Fonction - Read-only for staff (except DG), filled by admin */}
                <div className="space-y-1">
                  <span className="block text-sm font-medium text-gray-700">
                    <Briefcase className="inline w-4 h-4 mr-1" />
                    Fonction
                  </span>
                  {isStaffViewOnly ? (
                    <div className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-700">
                      {watch('fonction') || <span className="text-gray-400 italic">Non renseignée</span>}
                    </div>
                  ) : (
                    <input
                      type="text"
                      {...register('fonction')}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      placeholder="Ex: Directeur Pédagogique, Professeur, etc."
                    />
                  )}
                  {!isStaffViewOnly && errors.fonction && <p className="text-xs text-red-500">{errors.fonction.message}</p>}
                </div>

                {/* Diplôme - Searchable Dropdown */}
                <div className="space-y-1">
                  <span className="block text-sm font-medium text-gray-700">
                    <GraduationCap className="inline w-4 h-4 mr-1" />
                    Diplôme (selon nomenclature E-DEP)<span className="text-red-500 ml-1">*</span>
                  </span>
                  <Controller
                    name="diplomeId"
                    control={control}
                    render={({ field }) => {
                      const selectedDiplome = diplomes.find(d => d.id === field.value);
                      const filteredDiplomes = diplomes.filter(d =>
                        d.labelFr.toLowerCase().includes(diplomeSearch.toLowerCase())
                      );

                      return (
                        <div className="relative" ref={diplomeDropdownRef}>
                          <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <input
                              type="text"
                              value={showDiplomeDropdown ? diplomeSearch : (selectedDiplome?.labelFr || '')}
                              onChange={(e) => {
                                setDiplomeSearch(e.target.value);
                                setShowDiplomeDropdown(true);
                              }}
                              onFocus={() => {
                                setShowDiplomeDropdown(true);
                                setDiplomeSearch('');
                              }}
                              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                              placeholder="Rechercher un diplôme..."
                            />
                            {selectedDiplome && !showDiplomeDropdown && (
                              <button
                                type="button"
                                onClick={() => {
                                  field.onChange('');
                                  setDiplomeSearch('');
                                }}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            )}
                          </div>

                          {showDiplomeDropdown && (
                            <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                              {filteredDiplomes.length === 0 ? (
                                <div className="p-3 text-sm text-gray-500 text-center">
                                  Aucun diplôme trouvé
                                </div>
                              ) : (
                                filteredDiplomes.map(diplome => (
                                  <button
                                    key={diplome.id}
                                    type="button"
                                    onClick={() => {
                                      field.onChange(diplome.id);
                                      setDiplomeSearch('');
                                      setShowDiplomeDropdown(false);
                                    }}
                                    className={`w-full text-left px-4 py-2 hover:bg-gray-50 flex items-center justify-between ${
                                      field.value === diplome.id ? 'bg-primary-50 text-primary-700' : ''
                                    }`}
                                  >
                                    <span className="text-sm">{diplome.labelFr}</span>
                                    {field.value === diplome.id && (
                                      <Check className="w-4 h-4 text-primary-600" />
                                    )}
                                  </button>
                                ))
                              )}
                            </div>
                          )}
                        </div>
                      );
                    }}
                  />
                  {errors.diplomeId && <p className="text-xs text-red-500">{errors.diplomeId.message}</p>}
                </div>

                {/* Date de recrutement - Read-only for staff (except DG), filled by admin */}
                <div className="space-y-1">
                  <span className="block text-sm font-medium text-gray-700">
                    <Calendar className="inline w-4 h-4 mr-1" />
                    Date de recrutement{!isStaffViewOnly && <span className="text-red-500 ml-1">*</span>}
                  </span>
                  {isStaffViewOnly ? (
                    <div className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-700">
                      {watch('dateRecrutement') ? (toDisplayDate(watch('dateRecrutement')) || watch('dateRecrutement')) : <span className="text-gray-400 italic">Non renseignée</span>}
                    </div>
                  ) : (
                    <Controller
                      name="dateRecrutement"
                      control={control}
                      render={({ field }) => (
                        <DatePicker
                          value={field.value || ''}
                          onChange={field.onChange}
                          onBlur={field.onBlur}
                          error={errors.dateRecrutement?.message}
                          mode="date"
                        />
                      )}
                    />
                  )}
                  {!isStaffViewOnly && errors.dateRecrutement && <p className="text-xs text-red-500">{errors.dateRecrutement.message}</p>}
                </div>

                {/* RIB */}
                <div className="space-y-1">
                  <span className="block text-sm font-medium text-gray-700">
                    <CreditCard className="inline w-4 h-4 mr-1" />
                    RIB Bancaire<span className="text-red-500 ml-1">*</span>
                  </span>
                  <Controller
                    name="rib"
                    control={control}
                    render={({ field }) => (
                      <input
                        type="text"
                        value={formatRIB(field.value || '')}
                        onChange={(e) => {
                          // Store only digits, display formatted
                          const cleanValue = e.target.value.replace(/\D/g, '').slice(0, 24);
                          field.onChange(cleanValue);
                        }}
                        onBlur={field.onBlur}
                        className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 font-mono ${
                          errors.rib ? 'border-red-300' : 'border-gray-300'
                        }`}
                        placeholder="XXXX XXXX XXXX XXXX XXXX XXXX"
                        maxLength={29} // 24 digits + 5 spaces (visual formatting)
                      />
                    )}
                  />
                  <p className="text-xs text-gray-500">24 chiffres (format marocain)</p>
                  {errors.rib && <p className="text-xs text-red-500">{errors.rib.message}</p>}
                </div>

                {/* Salaire Mensuel Net - Read-only for staff (except DG), filled by admin */}
                {!isVacataire && (
                  <div className="space-y-1">
                    <span className="block text-sm font-medium text-gray-700">
                      <DollarSign className="inline w-4 h-4 mr-1" />
                      Salaire Mensuel Net
                    </span>
                    {isStaffViewOnly ? (
                      <div className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-700 flex justify-between items-center">
                        <span>{watch('salaireMensuelNet') ? formatSalaryMAD(watch('salaireMensuelNet') || '') : <span className="text-gray-400 italic">Non renseigné</span>}</span>
                        <span className="text-sm font-medium text-gray-500">MAD</span>
                      </div>
                    ) : (
                      <Controller
                        name="salaireMensuelNet"
                        control={control}
                        render={({ field }) => (
                          <div className="relative">
                            <input
                              type="text"
                              value={formatSalaryMAD(field.value || '')}
                              onChange={(e) => {
                                const numericValue = e.target.value.replace(/\D/g, '');
                                field.onChange(numericValue);
                              }}
                              onBlur={field.onBlur}
                              className="w-full px-4 py-2 pr-16 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                              placeholder="0"
                            />
                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm font-medium text-gray-500">
                              MAD
                            </span>
                          </div>
                        )}
                      />
                    )}
                    {!isStaffViewOnly && errors.salaireMensuelNet && <p className="text-xs text-red-500">{errors.salaireMensuelNet.message}</p>}
                  </div>
                )}
                {isVacataire && (
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-sm text-amber-800">
                      <DollarSign className="inline w-4 h-4 mr-1" />
                      Le salaire mensuel net est automatiquement fixé à 0 MAD pour les vacataires.
                    </p>
                  </div>
                )}

                {/* Campus de Paie - Only editable for DG */}
                {isDG && (
                  <div className="space-y-1">
                    <span className="block text-sm font-medium text-gray-700">
                      <Building2 className="inline w-4 h-4 mr-1" />
                      Campus de Paie
                    </span>
                    <Controller
                      name="payrollCampusId"
                      control={control}
                      render={({ field }) => (
                        <select
                          {...field}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        >
                          <option value="">Sélectionner un campus</option>
                          {campuses.map(campus => (
                            <option key={campus.id} value={campus.id}>
                              {campus.name}
                            </option>
                          ))}
                        </select>
                      )}
                    />
                    <p className="text-xs text-gray-500">Le campus responsable de votre paie</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* "Are you also a professor?" Question - For staff roles who can teach (not TEACHER or STUDENT/PARENT) */}
        {canAlsoBeProfesor && (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="px-4 border-b border-gray-100">
              <button
                type="button"
                onClick={() => toggleSection('alsoProf')}
                className="w-full flex items-center justify-between py-3 text-left"
              >
                <div className="flex items-center gap-2">
                  <GraduationCap className="w-5 h-5 text-primary-600" />
                  <span className="font-semibold text-gray-900">Activité d'enseignement</span>
                </div>
                {expandedSections.alsoProf ? (
                  <ChevronUp className="w-5 h-5 text-gray-400" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-gray-400" />
                )}
              </button>
            </div>

            {expandedSections.alsoProf && (
              <div className="p-4 space-y-4">
                <div className="space-y-2">
                  <span className="block text-sm font-medium text-gray-700">
                    Êtes-vous également professeur/enseignant ?{!isAcademicCoordinator && <span className="text-red-500 ml-1">*</span>}
                  </span>
                  {isAcademicCoordinator ? (
                    // View-only display for Academic Coordinators
                    <>
                      <p className="text-xs text-gray-500">
                        Cette information a été définie lors de la création de votre compte et ne peut pas être modifiée.
                      </p>
                      <Controller
                        name="isAlsoProfessor"
                        control={control}
                        render={({ field }) => (
                          <div className={`p-3 border rounded-lg ${
                            field.value === true
                              ? 'border-green-500 bg-green-50 text-green-700'
                              : 'border-gray-300 bg-gray-50 text-gray-700'
                          }`}>
                            <span className="font-medium">{field.value === true ? 'Oui' : 'Non'}</span>
                          </div>
                        )}
                      />
                    </>
                  ) : (
                    // Editable for other roles
                    <>
                      <p className="text-xs text-gray-500">
                        Si vous enseignez en plus de votre fonction principale, veuillez répondre Oui pour compléter les informations professeur.
                      </p>
                      <Controller
                        name="isAlsoProfessor"
                        control={control}
                        render={({ field }) => (
                          <div className="flex gap-4">
                            <label className={`flex-1 flex items-center justify-center gap-2 p-3 border rounded-lg cursor-pointer transition-colors ${
                              field.value === true
                                ? 'border-green-500 bg-green-50 text-green-700'
                                : 'border-gray-300 hover:border-gray-400'
                            }`}>
                              <input
                                type="radio"
                                checked={field.value === true}
                                onChange={() => {
                                  field.onChange(true);
                                  // Auto-set status to Permanent for staff who also teach
                                  setValue('statutProfesseur', 'P');
                                  // Notify parent for real-time document display
                                  onIsAlsoProfessorChange?.(true);
                                }}
                                className="sr-only"
                              />
                              <span>Oui</span>
                            </label>
                            <label className={`flex-1 flex items-center justify-center gap-2 p-3 border rounded-lg cursor-pointer transition-colors ${
                              field.value === false
                                ? 'border-gray-500 bg-gray-50 text-gray-700'
                                : 'border-gray-300 hover:border-gray-400'
                            }`}>
                              <input
                                type="radio"
                                checked={field.value === false}
                                onChange={() => {
                                  field.onChange(false);
                                  // Notify parent for real-time document display
                                  onIsAlsoProfessorChange?.(false);
                                }}
                                className="sr-only"
                              />
                              <span>Non</span>
                            </label>
                          </div>
                        )}
                      />
                      {errors.isAlsoProfessor && (
                        <p className="text-xs text-red-500 mt-1">{errors.isAlsoProfessor.message}</p>
                      )}
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Professor E-DEP Section - For TEACHER role OR staff who also teach */}
        {showProfessorFields && (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="px-4 border-b border-gray-100">
              <button
                type="button"
                onClick={() => toggleSection('professeur')}
                className="w-full flex items-center justify-between py-3 text-left"
              >
                <div className="flex items-center gap-2">
                  <GraduationCap className="w-5 h-5 text-primary-600" />
                  <span className="font-semibold text-gray-900">Informations E-DEP Professeur</span>
                </div>
                {expandedSections.professeur ? (
                  <ChevronUp className="w-5 h-5 text-gray-400" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-gray-400" />
                )}
              </button>
            </div>

            {expandedSections.professeur && (
              <div className="p-4 space-y-4">
                {/* Statut Professeur - Only shown for TEACHER role; staff who teach are automatically Permanent */}
                {isProfesseur ? (
                  <div className="space-y-1">
                    <span className="block text-sm font-medium text-gray-700">
                      Statut<span className="text-red-500 ml-1">*</span>
                    </span>
                    <Controller
                      name="statutProfesseur"
                      control={control}
                      render={({ field }) => (
                        <div className="flex gap-4">
                          <label className={`flex-1 flex items-center justify-center gap-2 p-3 border rounded-lg cursor-pointer transition-colors ${
                            field.value === 'P'
                              ? 'border-primary-500 bg-primary-50 text-primary-700'
                              : 'border-gray-300 hover:border-gray-400'
                          }`}>
                            <input
                              type="radio"
                              value="P"
                              checked={field.value === 'P'}
                              onChange={() => field.onChange('P')}
                              className="sr-only"
                            />
                            <span>Permanent</span>
                          </label>
                          <label className={`flex-1 flex items-center justify-center gap-2 p-3 border rounded-lg cursor-pointer transition-colors ${
                            field.value === 'V'
                              ? 'border-amber-500 bg-amber-50 text-amber-700'
                              : 'border-gray-300 hover:border-gray-400'
                          }`}>
                            <input
                              type="radio"
                              value="V"
                              checked={field.value === 'V'}
                              onChange={() => field.onChange('V')}
                              className="sr-only"
                            />
                            <span>Vacataire</span>
                          </label>
                        </div>
                      )}
                    />
                    {errors.statutProfesseur && <p className="text-xs text-red-500">{errors.statutProfesseur.message}</p>}
                  </div>
                ) : (
                  // For staff who also teach, status is automatically Permanent (read-only display)
                  <div className="space-y-1">
                    <span className="block text-sm font-medium text-gray-700">
                      Statut
                    </span>
                    <div className="p-3 bg-primary-50 border border-primary-200 rounded-lg text-primary-700">
                      <span className="font-medium">Permanent</span>
                      <span className="text-xs text-primary-500 ml-2">(automatique pour les responsables qui enseignent)</span>
                    </div>
                  </div>
                )}

                {/* Département d'attache - Searchable Dropdown */}
                <div className="space-y-1">
                  <span className="block text-sm font-medium text-gray-700">
                    <Building2 className="inline w-4 h-4 mr-1" />
                    Département d'attache
                  </span>
                  <Controller
                    name="departementAttacheId"
                    control={control}
                    render={({ field }) => {
                      const selectedDept = departements.find(d => d.id === field.value);
                      const filteredDepts = departements.filter(d =>
                        d.labelFr.toLowerCase().includes(deptSearch.toLowerCase())
                      );

                      return (
                        <div className="relative" ref={deptDropdownRef}>
                          <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <input
                              type="text"
                              value={showDeptDropdown ? deptSearch : (selectedDept?.labelFr || '')}
                              onChange={(e) => {
                                setDeptSearch(e.target.value);
                                setShowDeptDropdown(true);
                              }}
                              onFocus={() => {
                                setShowDeptDropdown(true);
                                setDeptSearch('');
                              }}
                              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                              placeholder="Rechercher un département..."
                            />
                            {selectedDept && !showDeptDropdown && (
                              <button
                                type="button"
                                onClick={() => {
                                  field.onChange('');
                                  setDeptSearch('');
                                }}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            )}
                          </div>

                          {showDeptDropdown && (
                            <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                              {filteredDepts.length === 0 ? (
                                <div className="p-3 text-sm text-gray-500 text-center">
                                  {departements.length === 0 ? 'Aucun département configuré' : 'Aucun département trouvé'}
                                </div>
                              ) : (
                                filteredDepts.map(dept => (
                                  <button
                                    key={dept.id}
                                    type="button"
                                    onClick={() => {
                                      field.onChange(dept.id);
                                      setDeptSearch('');
                                      setShowDeptDropdown(false);
                                    }}
                                    className={`w-full text-left px-4 py-2 hover:bg-gray-50 flex items-center justify-between ${
                                      field.value === dept.id ? 'bg-primary-50 text-primary-700' : ''
                                    }`}
                                  >
                                    <span className="text-sm">{dept.labelFr}</span>
                                    {field.value === dept.id && (
                                      <Check className="w-4 h-4 text-primary-600" />
                                    )}
                                  </button>
                                ))
                              )}
                            </div>
                          )}
                        </div>
                      );
                    }}
                  />
                  {errors.departementAttacheId && <p className="text-xs text-red-500">{errors.departementAttacheId.message}</p>}
                </div>

                {/* Grade - Searchable Dropdown */}
                <div className="space-y-1">
                  <span className="block text-sm font-medium text-gray-700">
                    <Award className="inline w-4 h-4 mr-1" />
                    Grade<span className="text-red-500 ml-1">*</span>
                  </span>
                  <Controller
                    name="gradeId"
                    control={control}
                    render={({ field }) => {
                      const selectedGrade = grades.find(g => g.id === field.value);
                      const filteredGrades = grades.filter(g =>
                        g.labelFr.toLowerCase().includes(gradeSearch.toLowerCase())
                      );

                      return (
                        <div className="relative" ref={gradeDropdownRef}>
                          <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <input
                              type="text"
                              value={showGradeDropdown ? gradeSearch : (selectedGrade?.labelFr || '')}
                              onChange={(e) => {
                                setGradeSearch(e.target.value);
                                setShowGradeDropdown(true);
                              }}
                              onFocus={() => {
                                setShowGradeDropdown(true);
                                setGradeSearch('');
                              }}
                              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                              placeholder="Rechercher un grade..."
                            />
                            {selectedGrade && !showGradeDropdown && (
                              <button
                                type="button"
                                onClick={() => {
                                  field.onChange('');
                                  setGradeSearch('');
                                }}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            )}
                          </div>

                          {showGradeDropdown && (
                            <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                              {filteredGrades.length === 0 ? (
                                <div className="p-3 text-sm text-gray-500 text-center">
                                  {grades.length === 0 ? 'Aucun grade configuré' : 'Aucun grade trouvé'}
                                </div>
                              ) : (
                                filteredGrades.map(grade => (
                                  <button
                                    key={grade.id}
                                    type="button"
                                    onClick={() => {
                                      field.onChange(grade.id);
                                      setGradeSearch('');
                                      setShowGradeDropdown(false);
                                    }}
                                    className={`w-full text-left px-4 py-2 hover:bg-gray-50 flex items-center justify-between ${
                                      field.value === grade.id ? 'bg-primary-50 text-primary-700' : ''
                                    }`}
                                  >
                                    <span className="text-sm">{grade.labelFr}</span>
                                    {field.value === grade.id && (
                                      <Check className="w-4 h-4 text-primary-600" />
                                    )}
                                  </button>
                                ))
                              )}
                            </div>
                          )}
                        </div>
                      );
                    }}
                  />
                  {errors.gradeId && <p className="text-xs text-red-500">{errors.gradeId.message}</p>}
                </div>

                {/* Spécialité */}
                <div className="space-y-1">
                  <span className="block text-sm font-medium text-gray-700">
                    <BookOpen className="inline w-4 h-4 mr-1" />
                    Spécialité<span className="text-red-500 ml-1">*</span>
                  </span>
                  <input
                    type="text"
                    {...register('specialite')}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Ex: Informatique de gestion, Marketing digital, etc."
                  />
                  <p className="text-xs text-gray-500">Spécialité selon votre diplôme</p>
                  {errors.specialite && <p className="text-xs text-red-500">{errors.specialite.message}</p>}
                </div>

                {/* Vacataire-specific fields */}
                {isVacataire && (
                  <>
                    {/* Est originaire d'un établissement public */}
                    <div className="space-y-1">
                      <span className="block text-sm font-medium text-gray-700">
                        Originaire d'un établissement public d'enseignement supérieur?<span className="text-red-500 ml-1">*</span>
                      </span>
                      <Controller
                        name="estOriginaireEtablissementPublic"
                        control={control}
                        render={({ field }) => (
                          <div className="flex gap-4">
                            <label className={`flex-1 flex items-center justify-center gap-2 p-3 border rounded-lg cursor-pointer transition-colors ${
                              field.value === 'O'
                                ? 'border-green-500 bg-green-50 text-green-700'
                                : 'border-gray-300 hover:border-gray-400'
                            }`}>
                              <input
                                type="radio"
                                value="O"
                                checked={field.value === 'O'}
                                onChange={() => field.onChange('O')}
                                className="sr-only"
                              />
                              <span>Oui</span>
                            </label>
                            <label className={`flex-1 flex items-center justify-center gap-2 p-3 border rounded-lg cursor-pointer transition-colors ${
                              field.value === 'N'
                                ? 'border-red-500 bg-red-50 text-red-700'
                                : 'border-gray-300 hover:border-gray-400'
                            }`}>
                              <input
                                type="radio"
                                value="N"
                                checked={field.value === 'N'}
                                onChange={() => field.onChange('N')}
                                className="sr-only"
                              />
                              <span>Non</span>
                            </label>
                          </div>
                        )}
                      />
                      {errors.estOriginaireEtablissementPublic && <p className="text-xs text-red-500">{errors.estOriginaireEtablissementPublic.message}</p>}
                    </div>

                    {/* Établissement d'origine */}
                    <div className="space-y-1">
                      <span className="block text-sm font-medium text-gray-700">
                        Établissement/Organisme d'origine<span className="text-red-500 ml-1">*</span>
                      </span>
                      <input
                        type="text"
                        {...register('etablissementOrigine')}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        placeholder="Ex: Université Mohammed V, Entreprise XYZ, etc."
                      />
                      {errors.etablissementOrigine && <p className="text-xs text-red-500">{errors.etablissementOrigine.message}</p>}
                    </div>

                    {/* Taux horaire: set by the administration (DG, DP, RF, BM), read-only for the person */}
                    <div className="space-y-1">
                      <span className="block text-sm font-medium text-gray-700">
                        <DollarSign className="inline w-4 h-4 mr-1" />
                        Taux horaire
                      </span>
                      <div className="w-full px-4 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-700">
                        {initialData?.tauxHoraire ? `${formatSalaryMAD(initialData.tauxHoraire)} MAD/h` : 'Non défini'}
                      </div>
                      <p className="text-xs text-gray-500">Défini par l'administration. Contactez la direction pour toute modification.</p>
                    </div>
                  </>
                )}

                {/* Matières enseignées - Read-only for all users, auto-generated by system */}
                <div className="space-y-1">
                  <span className="block text-sm font-medium text-gray-700">
                    <BookOpen className="inline w-4 h-4 mr-1" />
                    Matières enseignées
                  </span>
                  <div className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-700 min-h-[60px]">
                    {watch('matieresEnseignees') || <span className="text-gray-400 italic">Sera généré automatiquement après affectation des cours</span>}
                  </div>
                  <p className="text-xs text-gray-500">Ce champ est automatiquement mis à jour par le système après l'affectation des cours.</p>
                </div>
              </div>
            )}
          </div>
        )}

      </form>
    </div>
  );
});

// Add display name for debugging
ProfileForm.displayName = 'ProfileForm';

// Export form actions separately for use in parent
export const ProfileFormActions: React.FC<{
  onCancel?: () => void;
  onSaveProgress?: () => void;
  onSubmit: () => void;
  isLoading?: boolean;
}> = ({ onCancel, onSaveProgress, onSubmit, isLoading = false }) => (
  <div className="flex flex-wrap gap-3 pt-4">
    {onCancel && (
      <button
        type="button"
        onClick={onCancel}
        className="flex items-center justify-center gap-2 px-4 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
      >
        <X className="w-4 h-4" />
        Annuler
      </button>
    )}

    {onSaveProgress && (
      <button
        type="button"
        onClick={onSaveProgress}
        disabled={isLoading}
        className="flex items-center justify-center gap-2 px-4 py-2.5 border border-primary-300 text-primary-700 bg-primary-50 rounded-lg hover:bg-primary-100 disabled:opacity-50"
      >
        <Clock className="w-4 h-4" />
        Sauvegarder le progrès
      </button>
    )}

    <button
      type="button"
      onClick={onSubmit}
      disabled={isLoading}
      className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
    >
      {isLoading ? (
        <>
          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
          Enregistrement...
        </>
      ) : (
        <>
          <Save className="w-4 h-4" />
          Enregistrer le profil
        </>
      )}
    </button>
  </div>
);

export default ProfileForm;
